import { Combobox, InputBase, Loader, useCombobox } from '@mantine/core'
import { useEffect, useState } from 'react'

import { useGooglePlacesApi } from 'src/common/hooks'

import classes from './input-google-places.module.scss'

export interface InputGooglePlacesProps {
  value?: string
  label?: string
  placeholder?: string
  required?: boolean
  disabled?: boolean
  error?: any
  onChange?: (place: google.maps.places.PlaceResult | null) => void
  onInputChange?: (value: string) => void
  withAsterisk?: boolean
}

interface PlaceOption {
  value: string
  label: string
  placeId: string
}

const InputGooglePlaces = ({
  value = '',
  label,
  placeholder = 'Start typing an address...',
  required = false,
  disabled = false,
  error,
  onChange,
  onInputChange,
  withAsterisk,
}: InputGooglePlacesProps) => {
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  })

  const googlePlacesApi = useGooglePlacesApi()

  const [inputValue, setInputValue] = useState(value)
  const [options, setOptions] = useState<PlaceOption[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    setInputValue(value)
  }, [value])

  const handleInputChange = async (searchValue: string) => {
    setInputValue(searchValue)
    onInputChange?.(searchValue)

    if (searchValue.length > 2 && !loading) {
      setLoading(true)
      combobox.openDropdown()

      try {
        const results = await googlePlacesApi?.findPlace(searchValue)

        if (results?.predictions) {
          const newOptions = results.predictions.map((prediction) => ({
            value: prediction.description,
            label: prediction.description,
            placeId: prediction.place_id,
          }))
          setOptions(newOptions)
        } else {
          setOptions([])
        }
      } catch (error) {
        console.error('Error fetching places:', error)
        setOptions([])
      } finally {
        setLoading(false)
      }
    } else {
      setOptions([])
      combobox.closeDropdown()
    }
  }

  const handleOptionSubmit = (optionValue: string) => {
    const selectedOption = options.find(
      (option) => option.value === optionValue,
    )

    if (selectedOption && googlePlacesApi) {
      setInputValue(selectedOption.label)
      onInputChange?.(selectedOption.label)

      googlePlacesApi.getPlaceById(selectedOption.placeId, (result) => {
        onChange?.(result)
      })
    }

    combobox.closeDropdown()
  }

  const optionElements = options.map((option) => (
    <Combobox.Option value={option.value} key={option.placeId}>
      {option.label}
    </Combobox.Option>
  ))

  return (
    <Combobox
      store={combobox}
      onOptionSubmit={handleOptionSubmit}
      classNames={{
        dropdown: classes.dropdown,
        option: classes.option,
      }}
    >
      <Combobox.Target>
        <InputBase
          withAsterisk={withAsterisk}
          component="input"
          type="text"
          label={label}
          placeholder={placeholder}
          value={inputValue}
          onChange={(event) => handleInputChange(event.currentTarget.value)}
          onClick={() => combobox.openDropdown()}
          onFocus={() => combobox.openDropdown()}
          onBlur={() => combobox.closeDropdown()}
          required={required}
          disabled={disabled}
          error={error}
          rightSection={loading ? <Loader size={18} /> : null}
          rightSectionPointerEvents="none"
          classNames={{
            input: classes.input,
            label: classes.label,
            error: classes.error,
          }}
        />
      </Combobox.Target>

      <Combobox.Dropdown>
        <Combobox.Options>
          {loading ? (
            <Combobox.Option value="" disabled>
              <div className={classes.loadingOption}>
                <Loader size={16} />
                <span>Searching places...</span>
              </div>
            </Combobox.Option>
          ) : options.length > 0 ? (
            optionElements
          ) : (
            <Combobox.Option value="" disabled>
              <span className={classes.emptyOption}>
                {inputValue.length > 2
                  ? 'No places found'
                  : 'Type at least 3 characters'}
              </span>
            </Combobox.Option>
          )}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  )
}

export default InputGooglePlaces
